import type { ReactElement, ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { SidebarNav } from './SidebarNav.tsx'
import { AccountMenu } from './AccountMenu.tsx'
import { NotificationBell } from './NotificationBell.tsx'
import { ThemeToggle } from './ThemeToggle.tsx'
import { VaultLockButton } from './VaultLockButton.tsx'
import { breadcrumbFor, isVaultPath } from '../lib/nav.ts'
import type { NavProduct } from '../lib/nav.ts'

/**
 * The application chrome: sidebar, header, and the one `<main>` every page
 * renders into.
 *
 * It lives here rather than in `routes/__root.tsx` because the root route
 * also owns the document (`<html>`, the pre-paint theme script, the
 * devtools), and mixing the document with the chrome is what made the old
 * root a 145-line file that nothing could test. The shell takes no loader
 * data of its own, which is what makes it safe for the route error
 * boundary to render around a failed page.
 *
 * ONE CHILD BREAKS THE "MAKES NO REQUESTS" RULE, DELIBERATELY:
 * `NotificationBell` polls (D11). It has to live in the chrome rather
 * than on a vault page, because its entire purpose is reaching an
 * operator who is looking at something else — or at another tab. It is
 * self-contained: it owns its own fetches, every one of them is in a
 * try/catch, and a total failure renders a bell that sits at zero. The
 * shell itself still takes nothing and fetches nothing.
 *
 * One `useRouterState` subscription for the whole chrome, passed down as a
 * plain `pathname` string. Each child subscribing for itself would mean
 * four stores waking on every navigation to compute the same answer.
 */

export interface AppShellProps {
  /** The routed page. In the app this is the root route's `<Outlet />`. */
  children: ReactNode
  /**
   * The tenant's products, for the sidebar's entitlement filter, or
   * `null` while unknown (see `navForProducts`).
   *
   * The shell still makes no requests of its own — the root route's
   * loader resolves this before the chrome renders, which is what keeps
   * the sidebar stable instead of growing two sections a moment after
   * first paint.
   */
  products: readonly NavProduct[] | null
}

export function AppShell({ children, products }: AppShellProps): ReactElement {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  return (
    <div className="flex min-h-screen bg-canvas text-fg">
      {/* Visually hidden until focused. With a 15-link sidebar, a keyboard
          user otherwise tabs the entire nav on every page to reach the
          content — the single cheapest accessibility fix in the app. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-30 focus:rounded-md focus:border focus:border-accent focus:bg-elevated focus:px-3 focus:py-1.5 focus:text-sm focus:text-fg"
      >
        Skip to content
      </a>

      <SidebarNav pathname={pathname} products={products} />

      {/* `min-w-0` on the column, not just on the content: without it a
          wide table inside a flex child refuses to shrink and pushes the
          whole page into a horizontal scroll instead of scrolling itself. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex min-h-12 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-subtle bg-surface px-4 py-1.5">
          <ShellBreadcrumb pathname={pathname} />
          <div className="flex items-center gap-1.5">
            {/* Only on /vault/*: the control belongs where the key is in
                use. See `VaultLockButton`. */}
            {isVaultPath(pathname) && <VaultLockButton />}
            {/* `null` products means "could not find out" and fails
                OPEN, the same way `navForProducts` does: a bell that
                stays at zero costs nothing, while hiding it after a
                /hierarchy blip loses the operator their notifications
                for the session. */}
            <NotificationBell
              hasVault={products === null || products.includes('vault')}
            />
            <ThemeToggle />
            <AccountMenu />
          </div>
        </header>

        {/* Deliberately unpadded. Pages own their own padding today, and a
            padded `<main>` would double every one of them; the shell takes
            the padding over once the last page has moved to `PageHeader`. */}
        <main id="main" className="min-w-0 flex-1">
          {children}
        </main>
      </div>
    </div>
  )
}

/**
 * The header's page-title slot, derived from `NAV` rather than passed in.
 *
 * Deriving it means a page cannot forget to set it, and it cannot disagree
 * with the sidebar — both read the same list. The page's own `<h1>` stays
 * where it belongs, inside the page's `PageHeader`; this is orientation
 * above the fold, not a second heading, so it is a `<nav>` and not an
 * `<h*>` of any level.
 */
function ShellBreadcrumb({ pathname }: { pathname: string }): ReactElement {
  const trail = breadcrumbFor(pathname)

  // Off-nav pathnames (`/`, `/login`, a terminal pane) get the product
  // name instead of a crumb. An empty header reads as a rendering bug.
  if (trail.length === 0)
    return <span className="truncate text-xs text-faint">Stoaria Control</span>

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted">
        {trail.map((crumb, index) => (
          <li key={crumb} className="flex items-center gap-1.5">
            {index > 0 && (
              <span aria-hidden="true" className="text-faint">
                /
              </span>
            )}
            <span
              className={
                index === trail.length - 1 ? 'font-medium text-fg' : undefined
              }
            >
              {crumb}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  )
}
